/**
 * Diff indexing (spec §9.3). The server semantic differ (`POST /v1/flows/{id}/diff`) returns the
 * change class (C0–C4) and a typed change list, each keyed by `locus` (node id / edge id / metadata
 * field). We fold it into a per-element state map so the canvas can highlight added/changed elements
 * (removed elements no longer exist on the candidate canvas — they render in the structured list).
 */
import type { Change, ChangeClass, DiffResult } from '@/api/types';

export type DiffState = 'added' | 'removed' | 'changed';

const RANK: Record<DiffState, number> = { removed: 1, changed: 2, added: 3 };

/** Classify a server Change.type string into a coarse visual state. Conservative: unknown = changed. */
export function classifyChange(type: string): DiffState {
  const t = type.toUpperCase();
  if (/(ADD|INSERT|CREATE|NEW)/.test(t)) return 'added';
  if (/(REMOVE|DELETE|DROP)/.test(t)) return 'removed';
  return 'changed';
}

export interface DiffIndex {
  changeClass: ChangeClass;
  semanticNoOp: boolean;
  changes: Change[];
  byLocus: Map<string, DiffState>;
}

export function indexDiff(diff: DiffResult): DiffIndex {
  const byLocus = new Map<string, DiffState>();
  for (const c of diff.changes) {
    if (!c.locus) continue;
    const state = classifyChange(c.type);
    const prev = byLocus.get(c.locus);
    if (!prev || RANK[state] > RANK[prev]) byLocus.set(c.locus, state);
  }
  return {
    changeClass: diff.changeClass,
    semanticNoOp: diff.semanticNoOp,
    changes: diff.changes,
    byLocus,
  };
}

export const CHANGE_CLASS_META: Record<
  ChangeClass,
  { label: string; short: string; intent: 'neutral' | 'info' | 'attention' | 'negative' }
> = {
  C0_COSMETIC: { label: 'Cosmetic', short: 'C0', intent: 'neutral' },
  C1_PARAMETER: { label: 'Parameter', short: 'C1', intent: 'info' },
  C2_STRUCTURAL: { label: 'Structural', short: 'C2', intent: 'attention' },
  C3_AI_NODE: { label: 'AI node', short: 'C3', intent: 'attention' },
  C4_OBLIGATION: { label: 'Obligation', short: 'C4', intent: 'negative' },
};
